import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Home, Handshake, User, Settings, Users, DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile'
import { useUser, AccountType } from '@/contexts/UserContext'

// Mobile bottom nav: max 5 items per iOS HIG / Material Design.
// Signal + Progress accessible via the SliceAI Hub home page cards.
// Logout lives in Settings → Account (no duplicate destructive action in nav).
const getNavigationItems = (accountType: AccountType) => {
  switch (accountType) {
    case 'talent':
    case 'agent':
      return [
        { name: 'Home', href: '/slice-ai', icon: Home },
        { name: 'Marketplace', href: '/marketplace', icon: Handshake },
        { name: 'Wallet', href: '/wallet', icon: DollarSign },
        { name: 'Profile', href: '/profile', icon: User },
        { name: 'Settings', href: '/settings', icon: Settings },
      ];
    case 'brand':
    case 'venture':
      return [
        { name: 'Home', href: '/slice-ai', icon: Home },
        { name: 'Deals', href: '/deals-partnerships', icon: Handshake },
        { name: 'Talent', href: '/talent-marketplace', icon: Users },
        { name: 'Wallet', href: '/wallet', icon: DollarSign },
        { name: 'Settings', href: '/settings', icon: Settings },
      ];
    default:
      return [
        { name: 'Home', href: '/', icon: Home },
        { name: 'Profile', href: '/profile', icon: User },
        { name: 'Settings', href: '/settings', icon: Settings },
      ];
  }
}

export const MobileBottomNavigation: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isMobile } = useEnhancedMobile()
  const { accountType } = useUser()

  const navigationItems = getNavigationItems(accountType)

  if (!isMobile) return null

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[hsl(var(--sidebar-background))] border-t border-[hsl(var(--sidebar-border))] safe-area-bottom">
      <div className="flex items-center justify-around py-1 px-safe">
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.href
          const Icon = item.icon

          return (
            <button
              key={item.name}
              onClick={() => navigate(item.href)}
              className={cn(
                "flex flex-col items-center justify-center p-1.5 min-h-[44px] min-w-[44px] flex-1 touch-target transition-all duration-150",
                "active:scale-95",
                isActive
                  ? "text-[hsl(var(--sidebar-primary))]"
                  : "text-muted-foreground hover:text-[hsl(var(--sidebar-foreground))]"
              )}
              aria-label={item.name}
            >
              <Icon size={20} className="mb-0.5" />
              <span className="text-[11px] font-medium">{item.name}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}